import type { AxiosError, AxiosInstance } from 'axios'
import { useUserStore } from '@/config/store/userStore'
import router from '@/config/router'
import { useApplicationStore } from '@/config/store/applicationStore'
import { getApiErrorToast } from '@/services/application/apiErrorToast'
import i18n from '@/config/translation'

type ApiErrorResponse = {
  customMessage?: string
  message?: string
  errorKey?: string
}

let userStore
let applicationStore
const getUserStore = () => {
  if (!userStore)
    userStore = useUserStore()
  return userStore
}
const getApplicationStore = () => {
  if (!applicationStore)
    applicationStore = useApplicationStore()
  return applicationStore
}

const axiosRequestInterceptor = (axios: AxiosInstance) => {
  // Add a request interceptor
  axios.interceptors.request.use(
    (config) => {
      config.headers.authorization = `Bearer ${getUserStore()?.token}`
      return config
    },
    (error: AxiosError) =>
    // Do something with request error
      Promise.reject(error),
  )
  return axios
}

const axiosResponseInterceptor = (axios: AxiosInstance) => {
  // Add a response interceptor
  axios.interceptors.response.use(
    response =>
    // Any status code that lie within the range of 2xx cause this function to trigger
    // Do something with response data
      response,
    async (error: AxiosError<ApiErrorResponse>) => {
      if (error?.response?.status === 401) {
        getApplicationStore().pushError({
          title: error?.response?.data?.customMessage ?? i18n.global.t('services.api.errors.sessionExpired'),
          message: i18n.global.t('services.api.errors.loginAgain'),
          color: 'warning',
          duration: 5000,
        })
        
        if(!applicationStore.previousApplicationRoute && router.currentRoute.value?.name !== 'Login') applicationStore.previousApplicationRoute = router.currentRoute.value
        getUserStore().disconnection()
        await router.push({ name: 'Login' })
        return Promise.reject(error)
      }
      else if (error.message === 'Network Error') {
        getApplicationStore().pushError({
          title: i18n.global.t('services.api.errors.connection'),
          message: i18n.global.t('services.api.errors.connectionHint'),
          color: 'error',
          duration: 5000,

        })
      }
      else {
        const apiErrorToast = getApiErrorToast(error.response?.data)
        if (apiErrorToast) {
          getApplicationStore().pushError(apiErrorToast)
        }
        else if (error?.response?.status === 500) {
          getApplicationStore().pushError({
            title: i18n.global.t('services.api.errors.server'),
            message: error.response?.data?.customMessage ?? i18n.global.t('services.api.errors.contactTeam'),
            color: 'error',
            duration: 5000,

          })
        }
      }

      throw error
    },
  )
  return axios
}

export default (axios: AxiosInstance): AxiosInstance => {
  axios = axiosRequestInterceptor(axios)
  axios = axiosResponseInterceptor(axios)

  return axios
}
